import { useEffect, useState } from 'react';
import {
  BIRD_PORTRAIT,
  birdCode,
  DEFAULT_BIRD_POSE_ID,
  DEFAULT_SPECIES_ID,
  DEFAULT_PLUMAGE_ID,
  isPlumageId,
  type BirdPoseId,
  type SpeciesId,
} from '@dove/shared';
import { cn } from '@/lib/utils';
import { birdPortrait, birdPortraitNow } from '@/lib/bird-portrait';
import { BirdSkeleton } from './BirdSkeleton';
import { Pigeon } from './Pigeon';
import type { Worn } from './pigeon-cosmetics';

/**
 * How the square capture is laid over the box a caller sized.
 *
 * THE BOX HOLDS THE BIRD, NOT THE FRAME, and getting this wrong made every bird in
 * the product about a third smaller than the drawing it replaced. A capture is
 * `BIRD_PORTRAIT.FRAME_UNITS` tall in bird-heights and the bird itself is exactly 1
 * (`loadBird` normalises it), so drawing the capture at the box's own size draws the
 * bird at `1 / 1.45` of it — the rest is the headroom the shared frame keeps for a
 * pose with its wings out. Every width in the app was tuned against artwork that
 * filled its box, so the capture is enlarged until the BIRD fills it instead.
 *
 * DERIVED FROM THE SHARED PARAMETERS RATHER THAN FITTED BY EYE, so retuning the
 * frame or the eye level moves this with it. `scale = FRAME_UNITS` makes one
 * bird-height equal one box-height; the bird's feet sit
 * `(FRAME_UNITS / 2 − EYE_LEVEL) / FRAME_UNITS` up from the frame's bottom, so
 * scaling about the box's centre leaves them below its floor by a computable
 * fraction, and the translate puts them back on it.
 *
 * A CENTRED TRANSFORM RATHER THAN INSETS, and that is a bug fix rather than a
 * preference. This was `insetInlineStart` and a percentage `bottom` on a
 * `w-[145%]` image, which measured 37.8px right of centre in every tile of the
 * closet — a logical inset inside a subtree that most callers have mirrored with
 * `-scale-x-100` is not the symmetric offset it reads as. A scale about the
 * element's own centre cannot be asymmetric, in either direction, mirrored or not.
 *
 * THE TWO WIDE POSES SPILL, AND THAT IS WHY THERE ARE TWO FITS. `hopping` and
 * `flying` reach about 92% of the frame's width, so at this scale they overhang the
 * box by roughly a fifth on each side. Beside a name that is the licence the bird
 * has always had — `Pigeon` declares `overflow-visible`, and a bird spilling into a
 * card's padding costs the text nothing because this element is out of flow. **In a
 * grid of equal tiles it is wrong**: the closet's cells are `gap-2` apart, so a
 * spilling wing reaches its neighbour and the bird stops looking as though it is
 * *in* its own cell. So a grid asks for `frame` and gets the whole capture inside
 * the box, headroom included, which is the composition the capture was framed for
 * in the first place.
 */
const FIT: Record<'bird' | 'frame', React.CSSProperties> = (() => {
  const scale = BIRD_PORTRAIT.FRAME_UNITS;
  const feet =
    (BIRD_PORTRAIT.FRAME_UNITS / 2 - BIRD_PORTRAIT.EYE_LEVEL)
    / BIRD_PORTRAIT.FRAME_UNITS;
  /* Where the feet land, as a fraction of the box measured up from its floor,
     once the capture has been scaled about the box's centre. Negative — they hang
     below it — and the translate below is its negation. */
  const under = 0.5 + scale * (feet - 0.5);
  return {
    /* Read right to left: scale first, then lift. So the percentage is of the
       box's own height rather than of the scaled image's. */
    bird: { transform: `translateY(${(under * 100).toFixed(3)}%) scale(${scale})` },
    /* No transform at all: `inset-0` already puts the capture exactly on the box. */
    frame: {},
  };
})();

/**
 * A person's bird (docs/5 § D260806-1118).
 *
 * THE ONE WAY A BIRD IS DRAWN OUTSIDE THE SILL, and it is a photograph of the
 * actual model — the same GLB, the same recoloured atlas and the same lights that
 * stand on the sill, held at the posture its owner chose and captured
 * orthographically on the device. It replaces `Pigeon.tsx` at every surface where
 * the bird stands for a PERSON, which closes the debt § D260804-1313 took on and
 * § D260805-1237 restated: the studio bird and the byline bird were different
 * animals, and after a second species arrived the byline was drawing a pigeon for
 * people who keep a sparrow.
 *
 * THREE STATES, AND THE MIDDLE ONE IS NOT THE FALLBACK. That distinction was worth
 * getting right, because the first version drew the full-colour 2D bird while the
 * portrait resolved and it read badly: a *different* bird appeared for a moment and
 * then swapped, which is a flash of wrong content rather than a load.
 *
 *   - **Resolving** — a `BirdSkeleton`: the app's own skeleton, cut to a bird's
 *     outline. It says something bird-shaped is arriving and commits to nothing
 *     about which bird, because this component does not know yet. Only ever seen
 *     once per appearance per device, since a resolved capture is cached and the
 *     next render reads it synchronously.
 *   - **Resolved** — the portrait, faded in over the skeleton.
 *   - **No portrait at all** — the 2D `Pigeon`, in the right plumage. No WebGL, a
 *     capture that threw, a code from a server one release ahead: in that case the
 *     drawing is not a placeholder, it is the answer, permanently and silently.
 *     Which is why it is NOT what a load shows.
 *
 * ARIA: HIDDEN, unchanged from `Pigeon`. The bird sits beside a name that is
 * already read out, and announcing "Yusuf's indigo sparrow, glancing" on all twenty
 * rows of a feed is noise. It is decoration attached to an identity, and the
 * identity is the text.
 */

export function BirdPortrait({
  bird,
  plumage,
  species,
  pose,
  worn,
  mine = false,
  preview = false,
  fit = 'bird',
  halo = 0,
  className,
  style,
  ...props
}: Omit<React.ComponentProps<'span'>, 'children'> & {
  /**
   * The compiled appearance, straight off `PublicAuthor.bird`.
   *
   * PREFERRED OVER THE THREE PARTS, and every byline passes this: the server
   * compiled it, so every viewer of one person asks for the same address and the
   * shared capture actually gets shared. The parts below are for the closet, which
   * is drawing appearances that do not exist yet.
   */
  bird?: string;
  /** The plumage id, for the 2D bird underneath and for composing a code when
   *  `bird` is absent. Always pass it: it is what a device with no WebGL draws. */
  plumage?: string;
  species?: SpeciesId;
  pose?: BirdPoseId;
  /** Passed through to `Pigeon`. The cosmetics are parked (docs/5 § D260804-1313)
   *  and reach the portrait not at all, which is why they are not in the code. */
  worn?: Worn;
  /** Whether this is the viewer's own committed bird, and so whether this device
   *  may offer its capture to everybody else. See `lib/bird-portrait`. */
  mine?: boolean;
  /** Whether this appearance is uncommitted — a closet swatch. Renders locally and
   *  is never exchanged with the server. */
  preview?: boolean;
  /**
   * What the box holds: the BIRD at the size the 2D drawing was (`bird`, the
   * default, for every byline), or the whole capture including the frame's headroom
   * (`frame`, for a grid of tiles). See `FIT`.
   */
  fit?: 'bird' | 'frame';
  /** Knock-out ring on the 2D bird underneath. Ignored by the portrait, which sits
   *  on transparency and needs no separation from what is behind it. */
  halo?: number;
}) {
  /*
   * COMPOSED HERE ONLY WHEN THE SERVER DID NOT SEND ONE. Two callers need that: the
   * closet, which is asking what a bird would look like in a colour nobody has
   * saved, and any response cached before `bird` existed on the wire. Everything
   * else passes the server's own code — see the prop.
   */
  const code =
    bird
    ?? birdCode({
      species: species ?? DEFAULT_SPECIES_ID,
      plumage: isPlumageId(plumage) ? plumage : DEFAULT_PLUMAGE_ID,
      pose: pose ?? DEFAULT_BIRD_POSE_ID,
    });

  /* Seeded synchronously from what this session already knows, so a bird seen
     once is drawn on the FIRST render of every later row rather than fading in
     again on each one. `undefined` means "not resolved yet". */
  const [src, setSrc] = useState<string | null | undefined>(() =>
    birdPortraitNow(code),
  );

  useEffect(() => {
    const known = birdPortraitNow(code);
    if (known !== undefined) {
      setSrc(known);
      return;
    }
    let live = true;
    /* `preview` and `mine` are read at resolve time and never re-read, which is
       correct: they describe the ASK, not the appearance. */
    void birdPortrait(code, { mine, preview }).then((next) => {
      if (live) setSrc(next);
    });
    return () => {
      live = false;
    };
  }, [code, mine, preview]);

  return (
    <span
      data-slot="bird-portrait"
      aria-hidden
      /*
        `h-fit` IS LOAD-BEARING, AND IT IS THE FIX FOR A STRETCHED BIRD
        (docs/5 § D260808-0223). Almost every caller puts this in a flex row and sizes
        it by WIDTH alone — `w-[34px] shrink-0` — which leaves the height `auto`, and
        `align-items: stretch` is what a flex row does by default. So the box grew to
        the height of the ROW: on a notification whose sentence wrapped to two lines
        the span measured 34 × 79 instead of 34 × 32.8, and the capture below, which
        fills the box, was drawn two and a half times too tall.

        `fit-content` RATHER THAN `self-start`, which also stops the stretch. Cross
        sizing only stretches an item whose cross size is `auto`, and `fit-content`
        resolves to the same content height for a block box — so this makes the box
        unstretchable WITHOUT choosing an alignment, and the rows that centre their
        bird (`items-center`) keep centring it. `cn` is tailwind-merge, so a caller
        that does size the height — `size-8` on the gifts drawer's friend list —
        still wins.
      */
      className={cn('relative block h-fit', className)}
      style={style}
      {...props}
    >
      {/*
        THE 2D BIRD IS ALWAYS THE ELEMENT IN FLOW, whichever state is showing, and
        that is what holds the box open: every caller sizes this component by width
        and lets the height follow the artwork's aspect ratio. Swapping it out for
        the skeleton would collapse the row on the frame between the two.

        It is invisible unless it is the answer — `src === null`. While resolving,
        the skeleton is drawn over the top of it; once resolved, the portrait is.
      */}
      <Pigeon
        plumage={plumage}
        worn={worn}
        halo={halo}
        className={cn(
          'w-full transition-opacity duration-300 motion-reduce:transition-none',
          src === null ? 'opacity-100' : 'opacity-0',
        )}
      />
      {src === undefined && <BirdSkeleton className="absolute inset-0" />}
      {src && (
        <img
          src={src}
          alt=""
          /*
             ABSOLUTELY POSITIONED OVER THE 2D BIRD RATHER THAN REPLACING IT IN
             FLOW. `PIGEON_VIEWBOX` is 64 × 61.71, so the artwork is within 4% of
             square and the capture is square — one box holds both, and the box's
             height keeps coming from the artwork, which is what stops the row
             reflowing when a portrait lands.
          */
          /*
             `object-contain` SO THE CAPTURE CANNOT BE DISTORTED BY ITS BOX, ever,
             whatever a caller does to the box (docs/5 § D260808-0223). `h-fit` above
             fixes the one caller shape that was stretching it; this is the property
             that makes the guarantee general, because the default for an `<img>` is
             `fill` — the box's exact aspect, whatever that happens to be — and a bird
             is a drawing of an animal rather than a texture. A box the artwork does
             not fit now letterboxes inside it.

             It also corrects a stretch that was always there and too small to see:
             the capture is SQUARE and this box is the 2D artwork's 64 × 61.71, so the
             portrait was being drawn 3.7% wide on every surface in the app. Contain
             fits it to the box's height, which is the axis `FIT` does its arithmetic
             in, so the feet still land exactly where that comment computes.
          */
          /* `bird-keyline` is the capture's half of the hairline the 2D bird gets
             from a stroke — see index.css. It is a filter on the element rather
             than anything in the PNG, because one PNG is served to viewers on
             both themes. */
          className="bird-keyline absolute inset-0 block size-full object-contain transition-opacity duration-300 motion-reduce:transition-none"
          style={FIT[fit]}
          /* Decoding off the main thread, and eagerly: this is 256px of PNG that
             is about to be drawn at 44, and a decode on the frame it mounts is the
             one cost this whole cache exists to avoid paying twice. */
          decoding="async"
        />
      )}
    </span>
  );
}
